import React, { useState, useEffect } from 'react';
import {
    CalculatorContent,
    InputContainer,
    InputWrapper,
    UnitText,
    ResultDisplay,
    ResultTitle,
    ResultContent,
    ResultRow,
    ResultLabel,
    ResultValue,
    ResultHighlight,
    ResultNote,
} from './styles';
import CommonButton from '../button/CommonButton';
import { css } from '@emotion/react';
import { handleNumberInputChange, getNumericValue, addCommas } from '../../../utils/calculator/numberFormatter';

const AreaCalculator = ({ checkModalBoundary, adjustModalPosition }) => {
    const [squareMeters, setSquareMeters] = useState('');
    const [pyung, setPyung] = useState('');
    const [result, setResult] = useState(null);
    const [shouldCheckBoundary, setShouldCheckBoundary] = useState(false);

    const handleSquareMetersChange = (e) => {
        handleNumberInputChange(e, setSquareMeters, 6);
    };

    const handlePyungChange = (e) => {
        handleNumberInputChange(e, setPyung, 6);
    };

    useEffect(() => {
        if (shouldCheckBoundary && result) {
            const timer = setTimeout(() => {
                if (checkModalBoundary()) {
                    adjustModalPosition();
                }
                setShouldCheckBoundary(false);
            }, 0);

            return () => clearTimeout(timer);
        }
    }, [result, shouldCheckBoundary, checkModalBoundary, adjustModalPosition]);

    const convertToPyung = () => {
        if (!squareMeters) {
            alert('제곱미터를 입력해주세요.');
            return;
        }
        const sqm = getNumericValue(squareMeters);
        if (sqm < 0) {
            alert('음수는 입력할 수 없습니다.');
            return;
        }
        const pyungResult = (sqm / 3.3058).toFixed(2);
        const calculatedResult = {
            from: `${addCommas(sqm)} m²`,
            to: `${addCommas(pyungResult)} 평`,
            conversion: 'm² → 평',
        };
        setResult(calculatedResult);
        setShouldCheckBoundary(true);
    };

    const convertToSquareMeters = () => {
        if (!pyung) {
            alert('평을 입력해주세요.');
            return;
        }
        const pyungValue = getNumericValue(pyung);
        if (pyungValue < 0) {
            alert('음수는 입력할 수 없습니다.');
            return;
        }
        const sqmResult = (pyungValue * 3.3058).toFixed(2);
        const calculatedResult = {
            from: `${addCommas(pyungValue)} 평`,
            to: `${addCommas(sqmResult)} m²`,
            conversion: '평 → m²',
        };
        setResult(calculatedResult);
        setShouldCheckBoundary(true);
    };

    const clearAll = () => {
        setSquareMeters('');
        setPyung('');
        setResult(null);
    };

    return (
        <CalculatorContent>
            <div className="input-row">
                <div className="input-group">
                    <InputContainer>
                        <p>제곱미터(m²) → 평</p>
                        <InputWrapper>
                            <input
                                type="text"
                                placeholder="제곱미터 입력"
                                value={squareMeters}
                                onChange={handleSquareMetersChange}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <UnitText>m²</UnitText>
                        </InputWrapper>
                        <CommonButton
                            variant="secondary"
                            size="medium"
                            onClick={convertToPyung}
                            customStyles={css`
                                width: 100%;
                                margin-top: 10px;

                                &:hover {
                                    transform: translateY(-2px);
                                }
                            `}
                        >
                            평으로 변환
                        </CommonButton>
                    </InputContainer>
                </div>

                <div className="input-group">
                    <InputContainer>
                        <p>평 → 제곱미터(m²)</p>
                        <InputWrapper>
                            <input
                                type="text"
                                placeholder="평 입력"
                                value={pyung}
                                onChange={handlePyungChange}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <UnitText>평</UnitText>
                        </InputWrapper>
                        <CommonButton
                            variant="secondary"
                            size="medium"
                            onClick={convertToSquareMeters}
                            customStyles={css`
                                width: 100%;
                                margin-top: 10px;

                                &:hover {
                                    transform: translateY(-2px);
                                }
                            `}
                        >
                            m²로 변환
                        </CommonButton>
                    </InputContainer>
                </div>
            </div>

            <CommonButton
                variant="outlined"
                size="medium"
                onClick={clearAll}
                customStyles={css`
                    width: 100%;
                `}
            >
                초기화
            </CommonButton>

            {result && (
                <ResultDisplay>
                    <ResultTitle>변환 결과</ResultTitle>
                    <ResultContent>
                        <ResultRow>
                            <ResultLabel>변환 유형</ResultLabel>
                            <ResultValue>{result.conversion}</ResultValue>
                        </ResultRow>
                        <ResultRow>
                            <ResultLabel>입력값</ResultLabel>
                            <ResultValue>{result.from}</ResultValue>
                        </ResultRow>
                        <ResultRow>
                            <ResultLabel>변환 결과</ResultLabel>
                            <ResultHighlight>{result.to}</ResultHighlight>
                        </ResultRow>
                    </ResultContent>
                    <ResultNote>* 1평 = 3.3058m² 기준</ResultNote>
                </ResultDisplay>
            )}
        </CalculatorContent>
    );
};

export default AreaCalculator;
